import type { z } from 'zod';
import { ApiError } from '@/api/ApiError';
import { ErrorResponseSchema, type ErrorResponse } from 'schemas';
import { env } from '@/env';
import { handleApiError } from '@/api/helpers/handleApiError';
import { UNKNOWN_ERROR_API_ERROR } from './unknownError';
import { addApiBreadcrumb } from '@/utils/sentry';
import { isErrorResponse } from './isErrorResponse';

/**
 * Helper for making unauthenticated API requests with consistent error handling and Zod validation.
 * Throws ApiError for all error cases, returns the success data for valid responses.
 * Includes Sentry breadcrumbs and error tracking.
 *
 * @param method HTTP method (GET, POST, etc.)
 * @param path API path (e.g. '/api/waitlist')
 * @param params Request params (object for POST/PUT body, or query params for GET)
 * @param responseSchema Zod schema for the expected success response (discriminated union with 'type')
 * @param customHeaders Optional additional headers
 */
export async function unauthenticatedApiRequest<
  TSuccess,
  TResponse extends { type: string; data?: TSuccess },
>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  params?: Record<string, unknown>,
  responseSchema?: z.ZodType<TResponse>,
  customHeaders?: Record<string, string>
): Promise<TSuccess> {
  try {
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    let url = `${env.VITE_BACKEND_URL}${path}`;
    let fetchOptions: RequestInit = { method, headers };

    if (method === 'GET' && params && Object.keys(params).length > 0) {
      const query = new URLSearchParams(
        params as Record<string, string>
      ).toString();
      url += `?${query}`;
    } else if (params && method !== 'GET') {
      fetchOptions.body = JSON.stringify(params);
    }

    // Add API breadcrumb before request
    addApiBreadcrumb(method, path);

    const response = await fetch(url, fetchOptions);

    // Add API breadcrumb with response status
    addApiBreadcrumb(method, path, response.status);

    if (!response.ok) {
      let errorData: unknown;
      try {
        errorData = await response.json();
      } catch {
        throw UNKNOWN_ERROR_API_ERROR;
      }
      // Validate error response
      const parsed = ErrorResponseSchema.parse(errorData);
      throw new ApiError(parsed);
    }

    // Handle 204 No Content responses (no body)
    if (response.status === 204) {
      return undefined as TSuccess;
    }

    const rawData = await response.json();
    if (!responseSchema) return rawData;
    const validatedData = responseSchema.parse(rawData);
    if (isErrorResponse(validatedData)) {
      throw new ApiError(validatedData as ErrorResponse);
    }
    return validatedData.data as TSuccess;
  } catch (error) {
    // Pass method and path to handleApiError for Sentry tracking
    handleApiError(error, method, path, {
      requestParams: params,
      customHeaders: Object.keys(customHeaders || {}),
    });
  }
}
